import { useState, useEffect, useCallback } from 'react';
import { GetCompanyPayslipBranding, UpsertPayslipBranding } from '../../../../../api/Payroll/payslipBranding';
import { PayslipBrandingInput } from '../../../../../models/payroll';
import type {
  BrandingSettings,
  HeaderSettings,
  FooterSettings,
  LayoutSettings,
  Customization,
  LogoSettings,
} from '../types';
import { DEFAULT_BRANDING_SETTINGS } from '../types';

/** Safely parses a JSON string or returns the value as-is. */
const parseBrandingSetting = (setting: unknown): unknown => {
  if (!setting) return null;
  if (typeof setting === 'string') {
    try {
      return JSON.parse(setting);
    } catch {
      return setting;
    }
  }
  return setting;
};

export interface UseBrandingSettingsReturn {
  brandingSettings: BrandingSettings;
  isLoading: boolean;
  isSaving: boolean;
  setBrandingSettings: React.Dispatch<React.SetStateAction<BrandingSettings>>;
  loadBrandingSettings: () => Promise<void>;
  handleSave: () => Promise<void>;
  handleReset: () => void;
  updateHeaderSettings: (field: keyof HeaderSettings, value: unknown) => void;
  updateFooterSettings: (field: keyof FooterSettings, value: unknown) => void;
  updateLayoutSettings: (field: keyof LayoutSettings, value: unknown) => void;
  updateCustomization: (field: keyof Customization, value: unknown) => void;
  toggleBranding: () => void;
}

export function useBrandingSettings(
  onSettingsChange: (() => void) | undefined,
  showToast: (message: string, type: 'success' | 'error' | 'info') => void,
  t: (key: string, options?: Record<string, unknown>) => string,
): UseBrandingSettingsReturn {
  const [brandingSettings, setBrandingSettings] = useState<BrandingSettings>(DEFAULT_BRANDING_SETTINGS);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const loadBrandingSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await GetCompanyPayslipBranding();

      if (result.status === 200 && result.payload) {
        const payload = result.payload;
        const parsedLogo = parseBrandingSetting(payload.logoSettings) as Partial<LogoSettings> | null;
        const parsedHeader = parseBrandingSetting(payload.headerSettings) as Partial<HeaderSettings> | null;
        const parsedFooter = parseBrandingSetting(payload.footerSettings) as Partial<FooterSettings> | null;
        const parsedLayout = parseBrandingSetting(payload.layoutSettings) as Partial<LayoutSettings> | null;
        const parsedCustomization = parseBrandingSetting(payload.customization) as Partial<Customization> | null;

        setBrandingSettings({
          enableBranding: payload.enableBranding !== false,
          logoSettings: {
            hasLogo: parsedLogo?.hasLogo ?? false,
            logoUrl: parsedLogo?.logoUrl ?? null,
            logoPosition: parsedLogo?.logoPosition ?? 'top-left',
            logoSize: parsedLogo?.logoSize ?? 'medium',
            logoFile: null,
          },
          headerSettings: {
            companyName: parsedHeader?.companyName ?? 'Company Name LLC',
            headerText: parsedHeader?.headerText ?? 'Monthly Payslip',
            showDate: parsedHeader?.showDate ?? true,
            showPeriod: parsedHeader?.showPeriod ?? true,
            headerColor: parsedHeader?.headerColor ?? '#1f2937',
            headerBackgroundColor: parsedHeader?.headerBackgroundColor ?? '#f9fafb',
          },
          footerSettings: {
            footerText: parsedFooter?.footerText ?? 'This is an official document generated by the payroll system.',
            includeDisclaimer: parsedFooter?.includeDisclaimer ?? true,
            disclaimerText: parsedFooter?.disclaimerText ?? 'This document is confidential and intended for the addressee only.',
            showHelpContact: parsedFooter?.showHelpContact ?? true,
            helpContact: parsedFooter?.helpContact ?? 'hr@company.com',
            footerColor: parsedFooter?.footerColor ?? '#6b7280',
          },
          layoutSettings: {
            template: parsedLayout?.template ?? 'standard',
            colorScheme: parsedLayout?.colorScheme ?? 'professional',
            fontSize: parsedLayout?.fontSize ?? 'medium',
            showCompanyAddress: parsedLayout?.showCompanyAddress ?? true,
            showEmployeePhoto: parsedLayout?.showEmployeePhoto ?? false,
            paperSize: parsedLayout?.paperSize ?? 'A4',
            margins: parsedLayout?.margins ?? 'normal',
          },
          customization: {
            primaryColor: parsedCustomization?.primaryColor ?? '#2563eb',
            accentColor: parsedCustomization?.accentColor ?? '#059669',
            fontFamily: parsedCustomization?.fontFamily ?? 'Inter',
            borderStyle: parsedCustomization?.borderStyle ?? 'subtle',
            showWatermark: parsedCustomization?.showWatermark ?? false,
            watermarkText: parsedCustomization?.watermarkText ?? 'CONFIDENTIAL',
          },
        });
      }
    } catch (error) {
      console.error('Failed to load branding settings:', error);
      showToast(t('Failed to load branding settings'), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast, t]);

  useEffect(() => {
    loadBrandingSettings();
  }, [loadBrandingSettings]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const input: PayslipBrandingInput = {
        enableBranding: brandingSettings.enableBranding,
        logoSettings: JSON.stringify({
          ...brandingSettings.logoSettings,
          logoFile: undefined,
        }),
        headerSettings: JSON.stringify(brandingSettings.headerSettings),
        footerSettings: JSON.stringify(brandingSettings.footerSettings),
        layoutSettings: JSON.stringify(brandingSettings.layoutSettings),
        customization: JSON.stringify(brandingSettings.customization),
      };

      const result = await UpsertPayslipBranding(input);
      if (result.status === 200) {
        showToast(t('Payslip branding saved successfully'), 'success');
        onSettingsChange?.();
        await loadBrandingSettings();
      } else {
        showToast(t('Failed to save payslip branding'), 'error');
      }
    } catch (error) {
      console.error('Save error:', error);
      showToast(t('Failed to save payslip branding'), 'error');
    } finally {
      setIsSaving(false);
    }
  }, [brandingSettings, loadBrandingSettings, onSettingsChange, showToast, t]);

  const handleReset = useCallback(() => {
    setBrandingSettings(DEFAULT_BRANDING_SETTINGS);
    onSettingsChange?.();
    showToast(t('Branding settings reset to default'), 'info');
  }, [onSettingsChange, showToast, t]);

  const updateHeaderSettings = useCallback(
    (field: keyof HeaderSettings, value: unknown) => {
      setBrandingSettings((prev) => ({
        ...prev,
        headerSettings: { ...prev.headerSettings, [field]: value },
      }));
      onSettingsChange?.();
    },
    [onSettingsChange],
  );

  const updateFooterSettings = useCallback(
    (field: keyof FooterSettings, value: unknown) => {
      setBrandingSettings((prev) => ({
        ...prev,
        footerSettings: { ...prev.footerSettings, [field]: value },
      }));
      onSettingsChange?.();
    },
    [onSettingsChange],
  );

  const updateLayoutSettings = useCallback(
    (field: keyof LayoutSettings, value: unknown) => {
      setBrandingSettings((prev) => ({
        ...prev,
        layoutSettings: { ...prev.layoutSettings, [field]: value },
      }));
      onSettingsChange?.();
    },
    [onSettingsChange],
  );

  const updateCustomization = useCallback(
    (field: keyof Customization, value: unknown) => {
      setBrandingSettings((prev) => ({
        ...prev,
        customization: { ...prev.customization, [field]: value },
      }));
      onSettingsChange?.();
    },
    [onSettingsChange],
  );

  const toggleBranding = useCallback(() => {
    setBrandingSettings((prev) => ({
      ...prev,
      enableBranding: !prev.enableBranding,
    }));
  }, []);

  return {
    brandingSettings,
    isLoading,
    isSaving,
    setBrandingSettings,
    loadBrandingSettings,
    handleSave,
    handleReset,
    updateHeaderSettings,
    updateFooterSettings,
    updateLayoutSettings,
    updateCustomization,
    toggleBranding,
  };
}
